// src/components/KpiBigPanel.tsx (2026-06-15 新設)
// ★2026-08-22 ユーザ指示で「入院KPI」タブは廃止 (使わないので没)。
//   このファイルが残っているのは **colorForRatio / colorForYoy を
//   InpatientBudgetComparePanel と InpatientDoctorMatrix が使っている**ため。
//   `KpiBigPanel` コンポーネント自体はどこからも描画されない。復活させないこと
//   (入院実績タブが上位互換で、こちらは 直近12ヶ月平均 の母数が欠損月で目減りする問題を持つ)。
// 「入院KPI」タブ = 入院実績をシンプルにした大文字版。
//   8 セル (4 列 × 2 行)、 各セル = 大きい KPI (当月実績 + 予算 + 予算比 + 前年比) + ミニ月次推移。
//   サイドバーの 2 トグル (延数: 入院⇄在院 / 収入: 入院収入⇄粗利) で 収入・延数・単価カードが連動。
//   科別フォーカス時は 新規入院数 → 新規受持数 に差し替え。
import { useMemo, type ReactNode } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { useFilters, monthShift } from "../store";
import { fmtCardMain, fmtYenScaled, fmtYenAxisTick, isYenCardMetric, yenUseOku, fmtRatio, fmtRatioPct, ymJp } from "../lib/format";
import { PlotFigure } from "./Plot";
import { AllMetricsTable } from "./prototypes/AllMetricsTable";
import { BUDGET_TAB_SPECS } from "./prototypes/budgetSpec";
import { ContextBar } from "./ContextBar";
import { DeptMetricTable } from "./DeptMetricTable";
import { resolveAllSpecs, type ResolvedMetric } from "./prototypes/budgetSpec";
import { RATIO_METRICS, weightedRatio, isRatioMetric } from "../lib/ratioMetrics";
import type { KpiResponse, MetricRow, Direction, DeptMetricsAllResponse } from "@shared/api";
import { gpText } from "../lib/gpMode";

// 単位ごとの方向 (色判定用)。 dim_metric の direction を優先するため metrics から引く。
type Slot =
  | { kind: "native"; metric: string; label: string; specKey: string }
  | { kind: "ratio"; num: string; den: string; scale: number; unit: string; label: string; specKey: string };

const nat = (metric: string, label: string): Slot => ({ kind: "native", metric, label, specKey: metric });

// トグル + focusedDept から 8 スロットを解決
function resolveSlots(rev: "入院収入" | "粗利", vol: "入院" | "在院", focused: boolean): Slot[] {
  const revMetric = rev === "粗利" ? "GP" : "入院収入";
  const volMetric = vol === "在院" ? "在院患者延数" : "入院患者延数";
  let unitSlot: Slot;
  // 単価系は全て ratio(分子/分母)で扱う → 期間集計が Σ分子/Σ分母 で加重される (2026-06-30 比加重バグ修正)
  if (rev === "入院収入" && vol === "入院")      unitSlot = { kind: "ratio", num: "入院収入", den: "入院患者延数", scale: 1000, unit: "円/日", label: "1日入院単価", specKey: "入院単価" };
  else if (rev === "入院収入" && vol === "在院") unitSlot = { kind: "ratio", num: "入院収入", den: "在院患者延数", scale: 1000, unit: "円/日", label: "1日在院単価", specKey: "在院単価" };
  else if (rev === "粗利" && vol === "入院")     unitSlot = { kind: "ratio", num: "GP",       den: "入院患者延数", scale: 1000, unit: "円/日", label: gpText("1日粗利単価"), specKey: "GP単価" };
  // 在院 × 粗利 は元データに無いので GP ÷ 在院患者延数 (千円→円) で算出
  else unitSlot = { kind: "ratio", num: "GP", den: "在院患者延数", scale: 1000, unit: "円/日", label: gpText("1日粗利単価(在院)"), specKey: "GP単価_在院" };

  return [
    nat(revMetric, rev === "粗利" ? gpText("粗利") : "入院収入"),
    nat(volMetric, vol === "在院" ? "在院患者延数" : "入院患者延数"),
    unitSlot,
    nat("常勤換算数", "常勤医師数(入院診療科)"),
    nat(focused ? "新規受持数" : "新規入院数", focused ? "新規受持数" : "新規入院数"),
    nat("紹介入院率", "紹介入院率"),
    nat("平均在院日数", "平均在院日数"),
    nat("DPC期間2以内率", "DPCⅡ以内率"),
  ];
}

// 0 を「未確定」として null 扱いにする指標
const ZERO_IS_MISSING = new Set(["GP", "GP単価", "出来高換算薬剤費", "出来高換算材料費"]);

export function KpiBigPanel({ metrics, builtAt }: { metrics: MetricRow[]; builtAt: string }) {
  const f = useFilters();
  const avgMode = f.periodPreset === "直近12ヶ月平均";
  const slots = resolveSlots(f.revenueBasis, f.kpiVolumeBase, !!f.focusedDept);
  const ymLabel = `${f.asOfMonth.slice(2, 4)}年${f.asOfMonth.slice(5, 7)}月`;
  const headerLabel = f.focusedDept ? `🎯 ${f.focusedDept}` : "病院全体";

  const cards = (
    <section>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h2 className="text-xl font-bold">{headerLabel} 入院KPI（{avgMode ? "直近12ヶ月平均" : ymLabel}）</h2>
        <span className="text-xs text-slate-500 flex items-center gap-1.5">
          <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-[11px]">A</span>予算達成
          <span className="w-5 h-5 rounded-full bg-amber-400 text-white flex items-center justify-center font-bold text-[11px]">B</span>予算未達でも前年超
          <span className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-[11px]">C</span>それ以外
        </span>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 3, background: "#1B9E77", display: "inline-block" }}></span>実績</span>
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2.5px dashed #d97706", display: "inline-block" }}></span>予算</span>
          <span className="flex items-center gap-1"><span style={{ width: 16, height: 0, borderTop: "2px solid #94a3b8", opacity: 0.7, display: "inline-block" }}></span>前年</span>
        </div>
        {f.focusedDept && (
          <button onClick={() => f.setFocusedDept(null)}
            className="ml-auto text-sm px-3 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200 font-semibold">
            × フォーカス解除
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 print-4cols">
        {slots.map((s, i) => (
          <KpiBigCell key={i} slot={s} metrics={metrics} avgMode={avgMode} />
        ))}
      </div>
    </section>
  );

  // 印刷: page 1 = カード(4×2) + 月別マトリックス を 1 枚に集約、 page 2 = 科別実績。
  return (
    <div className="space-y-3">
      <KpiTables metrics={metrics} avgMode={avgMode} cards={cards} builtAt={builtAt} />
    </div>
  );
}

// 入院KPI タブ下部: 月別マトリックス (13 指標) + 注目指標の科別実績。
// 入院実績タブ (PrototypePanel) と同じ dept_metrics_all を共有 (queryKey 一致でキャッシュ共有)。
// マトリックスは KPI 13 指標に絞り、 科別実績は サイドバー「注目指標」(KPI 指標限定) に追従。
function KpiTables({ metrics, avgMode, cards, builtAt }: { metrics: MetricRow[]; avgMode: boolean; cards: ReactNode; builtAt: string }) {
  const f = useFilters();
  const targetName = f.focusedDept ? `🎯 ${f.focusedDept}` : "🏥 病院全体";
  // 直近12ヶ月平均モードはマトリックスのセルを12ヶ月移動平均にするため、 trend を24ヶ月に拡張 (trendBack=23)。
  const trendBack = avgMode ? 23 : 11;

  const { data, error } = useQuery<DeptMetricsAllResponse>({
    queryKey: ["dept_metrics_all", f.granularity, f.iryo, f.yotei, f.asOfMonth, f.periodFrom, f.periodTo, f.focusedDept, trendBack],
    queryFn: () => api.deptMetricsAll({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
      asOfMonth: f.asOfMonth, periodFrom: f.periodFrom,
      focusedDept: f.focusedDept ?? undefined, trendBack,
    }),
    placeholderData: (prev) => prev,
  });

  // マトリックスの指標 = KPIカードと一致 (収入/延数トグル・科別フォーカスに連動した 8 指標を同順で)
  const resolved = useMemo(() => {
    if (!data) return [];
    const keys = resolveSlots(f.revenueBasis, f.kpiVolumeBase, !!f.focusedDept).map((s) => s.specKey);
    const byKey = new Map(resolveAllSpecs(data).map((r) => [r.spec.key, r]));
    return keys.map((k) => byKey.get(k)).filter((r): r is ResolvedMetric => !!r);
  }, [data, f.revenueBasis, f.kpiVolumeBase, f.focusedDept]);

  return (
    <>
      {error && (
        <div className="card p-4 text-sm text-red-700 bg-red-50">
          データ取得エラー: {(error as Error).message}
        </div>
      )}
      {/* 印刷 page 1: カード8枚(4×2) + 月別マトリックス を 1 枚に集約 (行高を増やして A4 を活用)。
          先頭に条件ヘッダ (ContextBar の印刷コピー) を載せる (ユーザ要望 2026-06-24)。 */}
      <div className="budget-print-page kpi-page-1 space-y-3">
        <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
        {cards}
        {data && <AllMetricsTable resolved={resolved} targetName={targetName} avgMode={avgMode} />}
      </div>
      {/* 印刷 page 2: 注目指標の科別実績。 1枚目と同じ条件ヘッダを先頭に載せる。 */}
      {data && (
        <div className="budget-print-page kpi-page-2">
          <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
          <DeptMetricTable metrics={metrics} showBudget />
        </div>
      )}
    </>
  );
}

// ネイティブ指標は api.kpi をそのまま。 ratio 指標は num/den を取得して算出。
function useSlotData(slot: Slot, avgMode: boolean) {
  const f = useFilters();
  // 直近12ヶ月平均モードはグラフを12ヶ月移動平均にするため、 過去24ヶ月分を取得 (移動平均13点ぶん)。
  const periodFrom = avgMode ? monthShift(f.asOfMonth, -23) : f.periodFrom;
  const base = {
    granularity: f.granularity, iryo: f.iryo, yotei: f.yotei,
    asOfMonth: f.asOfMonth, periodFrom,
    focusedDept: f.focusedDept ?? undefined,
  };
  const isRatio = slot.kind === "ratio";
  const m1 = slot.kind === "native" ? slot.metric : slot.num;
  const m2 = slot.kind === "ratio" ? slot.den : "";
  const q1 = useQuery<KpiResponse>({
    queryKey: ["kpi", base.granularity, base.iryo, base.yotei, m1, base.asOfMonth, base.periodFrom, base.focusedDept],
    queryFn: () => api.kpi({ ...base, metric: m1 }),
    placeholderData: (p) => p,
  });
  const q2 = useQuery<KpiResponse>({
    queryKey: ["kpi", base.granularity, base.iryo, base.yotei, m2, base.asOfMonth, base.periodFrom, base.focusedDept],
    queryFn: () => api.kpi({ ...base, metric: m2 }),
    placeholderData: (p) => p,
    enabled: isRatio,
  });
  // native の比指標(平均在院日数/DPCⅡ以内率/紹介入院率 等)は registry の分子/分母を追加 fetch し、
  //   avgMode を Σ分子/Σ分母 で加重する(単月値・単位・色は native のまま不変・低リスク)。
  const natRatio = slot.kind === "native" && isRatioMetric(slot.metric) ? RATIO_METRICS[slot.metric] : null;
  const ratioOk = !!natRatio && !natRatio.needsData && !natRatio.unitDays && natRatio.den.length > 0;
  const ratioComps = ratioOk ? [...new Set([...natRatio!.num, ...natRatio!.den])] : [];
  const q3 = useQueries({
    queries: ratioComps.map((m) => ({
      queryKey: ["kpi", base.granularity, base.iryo, base.yotei, m, base.asOfMonth, base.periodFrom, base.focusedDept],
      queryFn: () => api.kpi({ ...base, metric: m }),
      placeholderData: (p: KpiResponse | undefined) => p,
    })),
  });
  return { q1, q2, q3, ratioSpec: ratioOk ? natRatio : null, ratioComps };
}

type Wide = { date: Date; actual: number | null; budget: number | null; prevYear: number | null;
  // ratio スロット用の生分子/分母 (期間集計を Σ分子/Σ分母 で加重するため)
  numA?: number | null; denA?: number | null; numB?: number | null; denB?: number | null; numP?: number | null; denP?: number | null };

function KpiBigCell({ slot, metrics, avgMode }: { slot: Slot; metrics: MetricRow[]; avgMode: boolean }) {
  const { q1, q2, q3, ratioSpec, ratioComps } = useSlotData(slot, avgMode);

  const nativeMetric = slot.kind === "native" ? slot.metric : slot.num;
  const def = metrics.find((x) => x["集計区分"] === nativeMetric);
  // [在院トグル fix 2026-06-28] 在院患者延数 等は dim_metric から意図的に除外(在院系は非表示方針)
  //   だが、在院トグルのカードには direction/unit が要る。budgetSpec(全KPI指標の定義)を fallback に。
  const specDef = BUDGET_TAB_SPECS.find((s) => s.key === nativeMetric);
  const unit = slot.kind === "ratio" ? slot.unit : (def?.unit ?? specDef?.unit ?? "");
  // 入院収入 / 粗利(GP) の card は 円 単位表示 (千円→円、 億円/万円に自動スケール、 単位を数値に併記)。
  //   下の表は千円のまま。 mini トレンドグラフの y軸も円(億/万)に揃える (ユーザ指示 2026-06-24)。
  const cardMetric = slot.kind === "native" ? slot.metric : undefined;
  const isYen = isYenCardMetric(cardMetric, unit);   // 入院収入/GP かつ unit=千円
  const gScale = isYen ? 1000 : 1;                    // グラフ: 千円→円
  const direction = (def?.direction ?? specDef?.direction ?? "neutral") as Direction;
  const zeroMissing = (m: string) => ZERO_IS_MISSING.has(m);
  const adj = (v: number | null | undefined, m: string): number | null =>
    v == null ? null : (zeroMissing(m) && v === 0 ? null : v);

  // 当月の current / budget / prevYear + 13ヶ月 trend を解決
  const { cur: cur0, bud: bud0, py: py0, wide } = useMemo(() => {
    const d1 = q1.data;
    if (slot.kind === "native") {
      const m = slot.metric;
      const w: Wide[] = (d1?.trend ?? []).map((p) => ({
        date: new Date(p.date),
        actual: adj(p.actual, m), budget: adj(p.budget, m), prevYear: adj(p.prevYear, m),
      }));
      return { cur: adj(d1?.current, m), bud: adj(d1?.budget, m), py: adj(d1?.prevYear, m), wide: w };
    }
    // ratio: num/den
    const d2 = q2.data;
    const ratio = (n: number | null, dn: number | null): number | null =>
      n == null || dn == null || dn === 0 ? null : (n * slot.scale) / dn;
    const numAdj = (v: number | null | undefined) => adj(v, slot.num);
    const cur = ratio(numAdj(d1?.current ?? null), d2?.current ?? null);
    const bud = ratio(numAdj(d1?.budget ?? null), d2?.budget ?? null);
    const py  = ratio(numAdj(d1?.prevYear ?? null), d2?.prevYear ?? null);
    const t2 = new Map((d2?.trend ?? []).map((p) => [p.date, p]));
    const w: Wide[] = (d1?.trend ?? []).map((p) => {
      const den = t2.get(p.date);
      return {
        date: new Date(p.date),
        actual: ratio(numAdj(p.actual), den?.actual ?? null),
        budget: ratio(numAdj(p.budget), den?.budget ?? null),
        prevYear: ratio(numAdj(p.prevYear), den?.prevYear ?? null),
        numA: numAdj(p.actual),   denA: den?.actual ?? null,
        numB: numAdj(p.budget),   denB: den?.budget ?? null,
        numP: numAdj(p.prevYear), denP: den?.prevYear ?? null,
      };
    });
    return { cur, bud, py, wide: w };
  }, [q1.data, q2.data, slot]);

  // 直近12ヶ月平均モード: 末尾12ヶ月の実績/予算/前年 の単純平均で 主数値・予算・前年 を置換。
  // (trend/チャートは月次のまま。 単月モードは current/budget/prevYear を使う)
  const mean12 = (sel: (w: Wide) => number | null): number | null => {
    const vals = wide.slice(-12).map(sel).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  // 比指標(ratio スロット)は月別比の単純平均でなく Σ分子/Σ分母 で加重 (2026-06-30 修正)。
  //   加算指標(native)は従来どおり月平均。
  const sum12 = (sel: (w: Wide) => number | null): number | null => {
    const vals = wide.slice(-12).map(sel).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };
  const wAvg = (selN: (w: Wide) => number | null, selD: (w: Wide) => number | null): number | null => {
    if (slot.kind !== "ratio") return null;
    const n = sum12(selN), d = sum12(selD);
    return n != null && d != null && d !== 0 ? (n * slot.scale) / d : null;
  };
  // native 比指標は registry 経由で Σ分子/Σ分母 加重(平均在院日数/DPC2率/紹介入院率)。単位・色は native のまま。
  const ratioCompData = new Map(ratioComps.map((m, i) => [m, q3[i]?.data] as const));
  const sum12c = (m: string, field: "actual" | "budget" | "prevYear"): number | null => {
    const d = ratioCompData.get(m); if (!d) return null;
    const vals = (d.trend ?? []).slice(-12).map((p) => p[field]).filter((v): v is number => v != null);
    return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
  };
  const natRatioAvg = (field: "actual" | "budget" | "prevYear"): number | null =>
    ratioSpec ? weightedRatio(ratioSpec, (m) => sum12c(m, field)) : null;
  const cur = avgMode ? (slot.kind === "ratio" ? wAvg((w) => w.numA ?? null, (w) => w.denA ?? null) : (ratioSpec ? natRatioAvg("actual")   : mean12((w) => w.actual)))   : cur0;
  const bud = avgMode ? (slot.kind === "ratio" ? wAvg((w) => w.numB ?? null, (w) => w.denB ?? null) : (ratioSpec ? natRatioAvg("budget")   : mean12((w) => w.budget)))   : bud0;
  const py  = avgMode ? (slot.kind === "ratio" ? wAvg((w) => w.numP ?? null, (w) => w.denP ?? null) : (ratioSpec ? natRatioAvg("prevYear") : mean12((w) => w.prevYear))) : py0;

  // 入院収入/粗利カードの 億/万 は「期間内(trend)の最大実績」で判定し、 数値/予算/グラフを固定単位に揃える
  //   (2026-06-24、 ユーザ指示: カードもマトリックスも期間内max で判定 → 当月値で割れない)。
  const yenMax = isYen ? Math.max(0, ...wide.map((w) => Math.abs(w.actual ?? 0))) : 0;
  const yenOku = isYen && yenUseOku(yenMax);
  const cf = (v: number | null | undefined) => isYen ? fmtYenScaled(v, yenOku) : fmtCardMain(v, unit, cardMetric);

  // グラフ系列: 単月モード = 月次 / 直近12ヶ月平均モード = 12ヶ月移動平均。 いずれも末尾12ヶ月を描画。
  const chartWide: Wide[] = useMemo(() => {
    if (!avgMode) return wide.slice(-12);
    const maAt = (sel: (w: Wide) => number | null, i: number): number | null => {
      const vals = wide.slice(i - 11, i + 1).map(sel).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    // 窓 (直近12ヶ月) がそろう i >= 11 の点だけ残す
    const out: Wide[] = [];
    for (let i = 11; i < wide.length; i++) {
      out.push({
        date: wide[i].date,
        actual:   maAt((x) => x.actual, i),
        budget:   maAt((x) => x.budget, i),
        prevYear: maAt((x) => x.prevYear, i),
      });
    }
    return out.slice(-12);
  }, [wide, avgMode]);

  const main = cf(cur);
  const ratioVal = bud != null && cur != null && bud !== 0 ? cur / bud : null;
  const ratioColor = colorForRatio(ratioVal, direction);
  const yoyColor = colorForYoy(cur, py, direction);
  const grade = gradeOf(cur, bud, py, direction);

  const opts = useMemo<Plot.PlotOptions>(() => {
    // 円カード(入院収入/GP)は グラフ y軸 も 円(億/万) に揃える (値 ×gScale、 tick/tooltip を円表記)。
    const tipFmt = (v: number | null) => {
      const f = cf(v);
      return isYen && f.value !== "—" ? `${f.value} ${f.unit}` : f.value;
    };
    const rows: { date: Date; value: number; series: "実績" | "予算" | "前年" }[] = [];
    for (const w of chartWide) {
      if (w.actual != null) rows.push({ date: w.date, value: w.actual * gScale, series: "実績" });
      if (w.budget != null) rows.push({ date: w.date, value: w.budget * gScale, series: "予算" });
      if (w.prevYear != null) rows.push({ date: w.date, value: w.prevYear * gScale, series: "前年" });
    }
    // x 軸 tick = データの実月から 3ヶ月おき (最新月を起点)。 "3 months" 間隔指定だと
    // カレンダー境界 (1/4/7/10月) に tick が打たれてデータ月とずれるため、 実 date を明示。
    const tickDates = chartWide.filter((_, i) => (chartWide.length - 1 - i) % 3 === 0).map((w) => w.date);
    return {
      width: 520, height: 150,
      marginLeft: 52, marginRight: 8, marginTop: 8, marginBottom: 24,
      style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" },
      x: { label: null, type: "time", ticks: tickDates, tickSize: 0, tickFormat: (d: Date) => `${d.getUTCMonth() + 1}月` },
      y: { label: null, grid: true, nice: true, zero: false,
           tickFormat: (v: number) => isYen ? fmtYenAxisTick(v, yenOku) : unit === "%" ? `${Math.round(v * 100)}%` : unit === "日" ? v.toFixed(1) : Math.round(v).toLocaleString() },
      marks: [
        Plot.lineY(rows.filter((d) => d.series === "前年"), { x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 2, strokeOpacity: 0.6 }),
        Plot.lineY(rows.filter((d) => d.series === "予算"), { x: "date", y: "value", stroke: "#d97706", strokeWidth: 2, strokeDasharray: "5 3" }),
        Plot.lineY(rows.filter((d) => d.series === "実績"), { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 3 }),
        Plot.dot(rows.filter((d) => d.series === "実績"), { x: "date", y: "value", fill: "#1B9E77", r: 2.8 }),
        Plot.tip(chartWide, Plot.pointerX({
          x: "date", y: (d: Wide) => (d.actual ?? d.prevYear ?? d.budget ?? 0) * gScale, fontSize: 12,
          title: (d: Wide) => [
            ymJp(d.date.toISOString()) + (avgMode ? " 時点の12ヶ月移動平均" : ""),
            `実績 : ${tipFmt(d.actual)}`,
            `予算 : ${tipFmt(d.budget)}`,
            `前年 : ${tipFmt(d.prevYear)}`,
          ].join("\n"),
        })),
      ],
    };
  }, [chartWide, unit, avgMode, isYen, gScale, cardMetric, yenOku]);

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm relative kpi-big-card">
      {grade && (
        <span
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-xl font-extrabold ${gradeStyle(grade)}`}
          title="予算達成=A / 予算未達でも前年超=B / それ以外=C">
          {grade}
        </span>
      )}
      <div className="text-base font-bold text-slate-700 pr-10">
        {slot.label}{!isYen && main.unit && <span className="text-slate-400 font-normal text-sm"> ({main.unit})</span>}
      </div>
      <div className="mt-1 text-4xl font-extrabold text-slate-900 leading-none kpi-big-num">
        {main.value}
        {isYen && main.value !== "—" && <span className="text-2xl font-bold text-slate-400 ml-1">{main.unit}</span>}
      </div>
      <div className="mt-2 flex items-baseline gap-3 flex-wrap text-sm">
        <span className="text-slate-500">予算 {cf(bud).value}{isYen && bud != null ? ` ${cf(bud).unit}` : ""}</span>
        <span className={`font-bold ${ratioColor}`}>予算比 {fmtRatio(ratioVal, 1)}</span>
        <span className={`font-bold ${yoyColor}`}>前年比 {fmtRatioPct(cur, py)}</span>
      </div>
      <div className="mt-2">
        <PlotFigure options={opts} responsiveWidth />
      </div>
    </div>
  );
}

// 達成度 A/B/C (2026-06-15 定義): 予算達成=A / 予算未達でも前年超=B / それ以外=C。
// (lower_is_better は「下回る」が達成)。 評価できる基準が無ければ null。
function gradeOf(cur: number | null, budget: number | null, prevYear: number | null, direction: Direction): "A" | "B" | "C" | null {
  if (cur == null || direction === "neutral") return null;
  const beats = (ref: number | null): boolean | null =>
    ref == null ? null : (direction === "lower_is_better" ? cur <= ref : cur >= ref);
  const budOk = beats(budget), pyOk = beats(prevYear);
  if (budOk === true) return "A";
  if (pyOk === true) return "B";
  if (budOk == null && pyOk == null) return null;
  return "C";
}
function gradeStyle(g: "A" | "B" | "C"): string {
  return g === "A" ? "bg-blue-600 text-white"
       : g === "B" ? "bg-amber-400 text-white"
       :             "bg-red-500 text-white";
}

// 2026-06-15: 予算比・前年比の色分けは 達成=青 / 未達=赤 の 2 色だけ (シンプル化)。
// (入院実績(前年度・予算比較) タブも同じ色分けを共有 — export)
export function colorForRatio(ratio: number | null, direction: Direction): string {
  if (ratio == null) return "text-slate-400";
  if (direction === "neutral") return "text-slate-600";
  const ok = direction === "lower_is_better" ? ratio <= 1 : ratio >= 1;
  return ok ? "text-blue-700" : "text-red-600";
}
export function colorForYoy(cur: number | null, py: number | null, direction: Direction): string {
  if (cur == null || py == null || py === 0) return "text-slate-400";
  if (direction === "neutral") return "text-slate-600";
  const ok = direction === "lower_is_better" ? cur <= py : cur >= py;
  return ok ? "text-blue-700" : "text-red-600";
}
